/**
 * Kraftpaw bundles (loaded on every page from snippets/kraftpaw-head.liquid)
 * - <kp-bundle-offer>: product-page choice between "product only" and "product + partner".
 *   When the bundle option is selected, the partner item is added right after the
 *   theme's own add-to-cart succeeds.
 * - form[data-kp-upsell-form]: "Complete the set" offer in the cart. It is a real
 *   /cart/add form, so it still works (via the cart page) if this script fails.
 * The bundle price itself comes from an automatic discount in Shopify; this file only adds items.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';

const money = (cents) => '$' + (Number(cents) / 100).toFixed(2);

function cartSections() {
  return Array.from(document.querySelectorAll('cart-items-component')).filter(
    (el) => el instanceof HTMLElement && el.dataset.sectionId
  );
}

/** Re-renders the cart drawer / cart page sections directly. */
function refreshCartSections() {
  for (const el of cartSections()) {
    const id = /** @type {HTMLElement} */ (el).dataset.sectionId;
    const isDrawer = /** @type {HTMLElement} */ (el).hasAttribute('data-drawer');
    try {
      sectionRenderer.renderSection(id, { cache: false, mode: isDrawer ? 'hydration' : 'full' });
    } catch (e) {
      /* ignore */
    }
  }
}

/**
 * Adds one variant to the cart and lets the theme refresh the cart drawer / cart page.
 * @param {string|number} variantId
 * @param {number} quantity
 * @param {Element} sourceEl
 * @returns {Promise<boolean>}
 */
async function kpAddToCart(variantId, quantity, sourceEl) {
  const deferred = CartLinesUpdateEvent.createPromise();
  sourceEl.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(variantId), quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity }],
        sections: cartSections()
          .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
          .join(','),
      }),
    });
    const data = await response.json();
    const cart = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((r) => r.json());

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'kp-bundle',
        sourceId: 'kp-bundle',
        itemCount: quantity,
        sections: data.status ? undefined : data.sections,
        didError: Boolean(data.status),
      },
    });
    return !data.status;
  } catch (error) {
    deferred.reject(error);
    return false;
  }
}

class KpBundleOffer extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.onKpChange);
    this.kpSync();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onKpChange);
  }

  /** @param {Event} event */
  onKpChange = (event) => {
    const target = event.target;
    if (target instanceof HTMLSelectElement && target.matches('[data-kp-partner-variant]')) {
      const bundleRadio = this.querySelector('input[type="radio"][value="bundle"]');
      if (bundleRadio instanceof HTMLInputElement) bundleRadio.checked = true;
    }
    this.kpSync();
  };

  get selected() {
    const checked = this.querySelector('input[type="radio"]:checked');
    return checked instanceof HTMLInputElement ? checked.value : 'single';
  }

  get partnerVariantId() {
    const select = this.querySelector('select[data-kp-partner-variant]');
    return select instanceof HTMLSelectElement ? select.value : this.dataset.partnerVariant;
  }

  kpSync() {
    this.querySelectorAll('[data-kp-option]').forEach((option) => {
      const input = option.querySelector('input[type="radio"]');
      option.classList.toggle('is-selected', input instanceof HTMLInputElement && input.checked);
    });

    const select = this.querySelector('select[data-kp-partner-variant]');
    if (select instanceof HTMLSelectElement) {
      const option = select.selectedOptions[0];
      const partnerPrice = Number((option && option.dataset.price) || this.dataset.partnerPrice);
      const total = Number(this.dataset.mainPrice) + partnerPrice - Number(this.dataset.saving || 0);
      const out = this.querySelector('[data-kp-bundle-total]');
      if (out) out.textContent = money(total);
    }
  }

  /** @param {{productId?: string|number, itemCount?: number}} detail */
  handleMainAdded(detail) {
    if (this.selected !== 'bundle') return;
    if (String(detail.productId) !== String(this.dataset.productId)) return;
    const quantity = Number(detail.itemCount) || 1;
    const variantId = this.partnerVariantId;
    if (variantId) kpAddToCart(variantId, quantity, this);
  }
}

if (!customElements.get('kp-bundle-offer')) {
  customElements.define('kp-bundle-offer', KpBundleOffer);
}

if (!window.kpBundleReady) {
  window.kpBundleReady = true;

  // Product page: after the theme adds the main product, add the partner if the bundle is selected.
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const promise = /** @type {any} */ (event).promise;
    if (!promise || typeof promise.then !== 'function') return;
    promise
      .then((result) => {
        const detail = result && result.detail;
        if (!detail || detail.didError || detail.source !== 'product-form-component') return;
        document.querySelectorAll('kp-bundle-offer').forEach((el) => {
          if (el instanceof KpBundleOffer) el.handleMainAdded(detail);
        });
      })
      .catch(() => {});
  });

  // Cart: "Complete the set" form.
  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('[data-kp-upsell-form]')) return;
      event.preventDefault();
      if (form.dataset.busy) return;
      form.dataset.busy = 'true';

      const button = form.querySelector('button[type="submit"]');
      if (button instanceof HTMLButtonElement) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      }

      const variantId = new FormData(form).get('id');
      const ok = variantId ? await kpAddToCart(String(variantId), 1, form) : false;

      if (!ok) {
        // Fall back to a normal form post (adds the item and opens the cart page).
        HTMLFormElement.prototype.submit.call(form);
        return;
      }

      // Safety net: if the cart did not refresh, re-render it; if that fails too, go to the cart page.
      setTimeout(() => {
        if (!document.body.contains(form)) return;
        refreshCartSections();
        setTimeout(() => {
          if (document.body.contains(form) && form.dataset.busy) window.location.href = Theme.routes.cart_url;
        }, 1500);
      }, 900);
    },
    true
  );

  // Do not reopen the cart drawer on the next page: it squeezed the page and shrank product photos.
  window.addEventListener('pagehide', () => {
    try {
      const ids = (sessionStorage.getItem('theme-drawer-open') || '').split(',').filter((id) => id && id !== 'cart-drawer');
      if (ids.length) sessionStorage.setItem('theme-drawer-open', ids.join(','));
      else sessionStorage.removeItem('theme-drawer-open');
    } catch (e) {
      /* ignore */
    }
  });

  // Let galleries and sliders re-measure after the drawer opens or closes.
  const remeasure = () => setTimeout(() => window.dispatchEvent(new Event('resize')), 400);
  document.addEventListener('theme-drawer:open', remeasure);
  document.addEventListener('theme-drawer:close', remeasure);
}
